import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { ExpandableDisclaimer } from "@/components/Compliance";
import usePageMeta from "@/hooks/usePageMeta";
import { COMPANY, REGULATORY_NOTICE } from "@/lib/site";

const FOUNDERS = [
  { name: "Hitesh Diwedi", role: "Co-Founder & CEO", initials: "HD", bio: "Leads vision, product strategy and platform direction, with a focus on making quantitative analytics understandable for market participants." },
  { name: "Gurjeet Singh", role: "Co-Founder & COO", initials: "GS", bio: "Drives operations, execution and organisational scaling across engineering, research and go-to-market." },
];

export default function About() {
  usePageMeta({
    title: "About — Adviserz, a brand of Quant Core Technologies Private Limited",
    description:
      "Adviserz is a brand name of Quant Core Technologies Private Limited — a financial technology, quantitative analytics and market intelligence platform. Not registered with SEBI as a Research Analyst or Investment Adviser.",
    canonical: "https://adviserz.in/about",
  });

  return (
    <div className="min-h-screen bg-surface-base text-white">
      <SiteHeader active="about" />

      <section className="relative overflow-hidden border-b border-white/[0.06]">
        <div className="absolute inset-0 grid-bg grid-radial-mask" />
        <div className="absolute inset-0 spotlight-column opacity-70" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-12 pt-36 md:pt-44 pb-20">
          <div className="overline mb-6">About</div>
          <h1 className="font-display font-medium text-[38px] sm:text-5xl lg:text-[60px] leading-[1.05] tracking-tighter max-w-4xl">
            A financial technology company building <span className="brand-text-gradient">market intelligence infrastructure.</span>
          </h1>
          <p className="mt-8 text-base md:text-lg text-zinc-400 max-w-3xl leading-relaxed">
            {COMPANY.disclosure} We build quantitative research infrastructure, analytics and AI-powered market intelligence
            tools intended to help market participants understand financial markets with greater depth.
          </p>
        </div>
      </section>

      {/* Entity disclosure */}
      <section className="bg-surface-base border-b border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <div className="label-xs mb-8">Corporate identity</div>
          <div className="grid md:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06] rounded-xl overflow-hidden" data-testid="entity-disclosure">
            <Cell k="Legal Entity" v={COMPANY.legalName} />
            <Cell k="Brand / Trade Name" v={COMPANY.brand} />
            <Cell k="Business" v={COMPANY.business} />
          </div>
          <p className="mt-6 text-xs text-zinc-500 leading-relaxed max-w-3xl">
            Adviserz is not a separate legal entity. It is the brand name under which {COMPANY.legalName} operates its
            technology, analytics and market intelligence products.
          </p>

          <div className="mt-10 rounded-lg border border-white/[0.08] bg-white/[0.015] p-6" data-testid="about-regulatory-notice">
            <div className="label-xs text-ai-accent mb-3">Regulatory Status</div>
            <p className="text-sm text-zinc-400 leading-relaxed">{REGULATORY_NOTICE.replace("Regulatory Status: ", "")}</p>
            <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
              Accordingly, the platform does not offer investment advisory services, research reports, personalised
              recommendations or paid trading signals, and does not collect payment for any such service.
            </p>
          </div>
        </div>
      </section>

      {/* What we do / don't */}
      <section className="bg-surface-base border-b border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-24 grid lg:grid-cols-2 gap-10">
          <div>
            <div className="label-xs mb-5">What we do</div>
            <ul className="space-y-3">
              {[
                "Financial technology and analytics engineering",
                "Quantitative research infrastructure",
                "Market intelligence and data visualisation",
                "Derivatives, volatility and market-structure analytics",
                "AI-powered financial news intelligence",
                "Educational and explanatory market content",
              ].map((x) => (
                <li key={x} className="rounded-md border border-white/[0.08] bg-white/[0.02] px-4 py-3.5 text-sm text-zinc-300">{x}</li>
              ))}
            </ul>
          </div>
          <div>
            <div className="label-xs mb-5">What we do not do</div>
            <ul className="space-y-3">
              {[
                "Investment advisory or research analyst services",
                "Personalised investment recommendations",
                "Buy / sell / hold calls or trading tips",
                "Portfolio management or fund management",
                "Guaranteed, fixed or assured return claims",
                "Brokerage, execution or custody of funds",
              ].map((x) => (
                <li key={x} className="rounded-md border border-white/[0.08] bg-white/[0.015] px-4 py-3.5 text-sm text-zinc-500">{x}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Founders */}
      <section className="bg-surface-base border-b border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <div className="label-xs mb-8">Founding team</div>
          <div className="grid md:grid-cols-2 gap-5">
            {FOUNDERS.map((f, i) => (
              <div key={f.name} data-testid={`about-founder-${i}`} className="trace-border rounded-xl p-8">
                <div className="flex items-start gap-6">
                  <div className="shrink-0 w-16 h-16 rounded-xl brand-gradient grid place-items-center font-display font-medium text-xl text-white">
                    {f.initials}
                  </div>
                  <div>
                    <h3 className="font-display font-medium text-xl tracking-tight">{f.name}</h3>
                    <div className="mt-1 overline text-ai-accent">{f.role}</div>
                    <p className="mt-4 text-sm text-zinc-400 leading-relaxed">{f.bio}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact + CTA */}
      <section className="relative bg-surface-base overflow-hidden">
        <div className="absolute inset-0 ambient-glow" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-24 grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <div className="label-xs mb-5">Registered contact</div>
            <p className="text-sm text-zinc-300">{COMPANY.legalName}</p>
            <p className="mt-1 text-sm text-zinc-500">Brand: {COMPANY.brand}</p>
            <p className="mt-3 text-sm text-zinc-400 leading-relaxed max-w-sm">{COMPANY.address}</p>
            <a href={`mailto:${COMPANY.email}`} className="mt-3 inline-block text-sm text-white hover:underline">{COMPANY.email}</a>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/technology" data-testid="about-cta-technology"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-white/15 text-white hover:bg-white/[0.05] transition-colors text-sm">
                Explore Our Technology <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/early-access" data-testid="about-cta-early-access"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md brand-gradient text-white text-sm font-medium hover:brightness-110 transition-all">
                Join Early Access
              </Link>
            </div>
          </div>
          <ExpandableDisclaimer testid="about-disclaimer" />
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

function Cell({ k, v }) {
  return (
    <div className="bg-[#0B0C10] p-8">
      <div className="label-xs text-zinc-500">{k}</div>
      <div className="mt-3 text-base text-white leading-relaxed">{v}</div>
    </div>
  );
}
